import React from 'react';
import { Download, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { buildCSV, downloadCSV, downloadNodePNG, fileDate, slug } from './exportUtils';

/**
 * Same Export dropdown as the Contamination Rate card (Download as CSV / PNG).
 *
 * Props
 *  - cardRef:  ref of the card's root element (used for the PNG)
 *  - title:    chart title (CSV header + file name)
 *  - meta:     { period, bins, wasteType? } written at the top of the CSV
 *  - getData:  () => ({ headers: string[], rows: any[][] })
 *  - compact:  icon-only trigger (for small cards)
 */
export function CardExportMenu({ cardRef, title, meta, getData, compact = false, disabled = false }) {
    const { t } = useTranslation();

    const handleCSV = () => {
        const { headers, rows } = getData();
        downloadCSV(buildCSV(title, meta, headers, rows), `${slug(title)}_${fileDate()}.csv`);
    };

    const handlePNG = async () => {
        if (!cardRef?.current) return;
        try {
            await downloadNodePNG(cardRef.current, `${slug(title)}_${fileDate()}.png`);
        } catch (err) {
            console.error('Error exporting PNG:', err);
        }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    className="export-controls text-muted-foreground hover:text-primary h-7 px-2 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Download className="h-4 w-4" />
                    {!compact && <span className="text-xs ml-1">{t('exportlabel', 'Export')}</span>}
                    {!compact && <ChevronDown className="h-3 w-3 ml-1" />}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleCSV}>{t('downloadAsCSV', 'Download as CSV')}</DropdownMenuItem>
                <DropdownMenuItem onClick={handlePNG}>{t('downloadAsPNG', 'Download as PNG')}</DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default CardExportMenu;